import styled, { css } from "styled-components";
import { FC, useState } from "react";
import { AnimatePresence } from "framer-motion";

import Button from "@/components/Button";
import ScriptButton from "./ScriptButton";
import ScriptCreationModal from "./ScriptCreationModal";
import { ProductionScriptResource } from "@/types/resources";
import VideoReferenceBlock from "./VideoReferenceBlock";

const LinkBox = styled.div<{ $hasOneOnly: boolean }>`
  padding: 16px;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  margin-bottom: 12px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
  ${({ $hasOneOnly }) =>
    $hasOneOnly &&
    css`
      display: block;
    `}
`;

const ScriptSelectionBox = styled.div`
  display: flex;
  margin-bottom: 12px;
`;

const StyledButton = styled(Button)`
  height: 40px;
  padding: 0;
  width: 40px;
  flex-shrink: 0;
  margin-left: 8px;
`;

const ScriptSelections = styled.div`
  flex: 1;
  display: flex;
  gap: 8px;
  overflow: auto;
`;

interface Props {
  activeScriptId: number | null;
  setActiveScriptId: (scriptId: number | null) => void;
  scripts: ProductionScriptResource[];
}

const ScriptSelection: FC<Props> = ({
  activeScriptId,
  setActiveScriptId,
  scripts,
}) => {
  const [showModal, setShowModal] = useState(false);

  const videoReferences =
    scripts.find((s) => s.id === activeScriptId)?.videoReferences ?? [];

  return (
    <>
      <ScriptSelectionBox>
        <ScriptSelections>
          {scripts.map((script) => (
            <ScriptButton
              key={script.id}
              script={script}
              active={script.id === activeScriptId}
              setActiveScriptId={setActiveScriptId}
            />
          ))}
        </ScriptSelections>
        <StyledButton color="secondary" onClick={() => setShowModal(true)}>
          <svg
            width="15"
            height="15"
            viewBox="0 0 15 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3.5 7.5H11.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M7.5 3.5L7.5 11.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </StyledButton>
      </ScriptSelectionBox>
      {!!videoReferences.length && (
        <LinkBox $hasOneOnly={videoReferences.length === 1}>
          {videoReferences.map((reference, i) => (
            <VideoReferenceBlock videoReference={reference} key={i} />
          ))}
        </LinkBox>
      )}
      <AnimatePresence>
        {showModal && (
          <ScriptCreationModal onClose={() => setShowModal(false)} />
        )}
      </AnimatePresence>
    </>
  );
};

export default ScriptSelection;
